// Work in flight that a release swap must wait for (plan frontend-release-update §4.1,
// 2026-10-21).
//
// A new release is loaded by a full page load, and a page load abandons whatever the page was
// doing: a campaign save between its creative uploads and its plan write, an upload half sent,
// the read-back that recovers a write whose response was lost. So every swap, whether the
// banner's Reload, the next page change or a hidden tab's quiet reload, waits while a hold is
// in place.
//
// A hold lives in this module, not in a component: the campaign save goes on after the
// operator leaves the builder, and its hold must outlive the screen that started it. Any
// mutation the query client is running counts too, so a write nobody wrapped still holds.
//
// What a hold does not cover is a draft nobody has saved yet. That is the view-page rule in
// app-release.ts: SAM never replaces a page that may hold one unless the operator asks.

import { queryClient } from '@/cache/queryClient';

/** What is holding the swap; named so a stuck hold can be told apart in a debugger. */
export type ReleaseHoldReason =
  | 'campaign_save'
  | 'creative_upload'
  | 'creative_save'
  | 'creative_content_save'
  | 'creative_file_accept';

const holds = new Map<symbol, ReleaseHoldReason>();
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) listener();
}

// A mutation starting or settling changes the answer too.
queryClient.getMutationCache().subscribe(() => emit());

/**
 * Hold every release swap until the returned function is called. Call it in a `finally`; a
 * second call does nothing.
 */
export function holdRelease(reason: ReleaseHoldReason): () => void {
  const key = Symbol(reason);
  holds.set(key, reason);
  emit();
  return () => {
    if (holds.delete(key)) emit();
  };
}

/** Whether anything is holding the swap now: a hold, or a mutation in flight. */
export function releaseHeld(): boolean {
  return holds.size > 0 || queryClient.isMutating() > 0;
}

/** The reasons held now, for tests and debugging. */
export function releaseHoldReasons(): ReleaseHoldReason[] {
  return [...holds.values()];
}

/** Called whenever a hold or a mutation starts or ends. */
export function subscribeReleaseHolds(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
